import { faHistory, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { Checkbox, DatePicker, YesNoDialog, Toast, Modal, ModalBody, ModalFooter, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import Banner from '@/components/Banner';
import { Button } from '@/components/Buttons';
import { ProjectRelativeLink } from '@/components/navigation';
import { orderBy } from 'lodash-es';
import React, { useState } from 'react';
import { revertUnitDeadlines } from '@/services/projects';
import {
    getDeadlineForResidentialUnitAndDeadline,
    getDeadlinesForResidentialUnit,
    saveResidentialUnitDeadlines,
} from '@/services/residential-units';
import { ResidentialUnitDeadline } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { classnames } from '@/utils/html';
import theme from './DeadlineEditDialog.module.scss';

interface DeadlineEditDialogProps {
    onClose(): void;
    onAccept?(): void;
    show: boolean;
    residentialUnitId: number;
    deadlineId?: number;
}

const DeadlineEditDialog: React.FC<DeadlineEditDialogProps> = ({
    show,
    onClose,
    onAccept,
    residentialUnitId,
    deadlineId,
}) => {
    const [saving, setSaving] = useState(false);
    const [deadlines, setDeadlines] = useState<ResidentialUnitDeadline[]>([]);
    const [showRevertDialog, setShowRevertDialog] = useState(false);

    const handleRevertOpen = () => {
        setShowRevertDialog(true);
    };

    const handleRevertClose = () => {
        setShowRevertDialog(false);
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleRevertAll = async () => {
        setSaving(true);
        deadlines
            .filter((x) => x.isEdited)
            .forEach((x) => {
                if (x.residentialUnitId && x.residentialUnitDeadlineId) {
                    revertUnitDeadlines(x.residentialUnitId, x.id);
                }
            });
        setSaving(false);
        Toast.success(<Trans>Reverted successfully</Trans>);
        triggerHandler(onAccept);
    };

    const handleAccept = async (e) => {
        setSaving(true);
        deadlines
            .filter((x) => x.isEdited === false)
            .forEach((x) => {
                if (x.residentialUnitId && x.residentialUnitDeadlineId) {
                    revertUnitDeadlines(x.residentialUnitId, x.id);
                }
            });
        await saveResidentialUnitDeadlines(
            residentialUnitId,
            deadlines.filter((x) => x.isEdited)
        );
        setSaving(false);
        Toast.success(<Trans>Updated successfully</Trans>);
        triggerHandler(onAccept);
    };

    const changeDate = (date, deadline) => {
        setDeadlines(
            deadlines.map((dl) => {
                if (dl.id === deadline.id) return { ...dl, deadline: date };
                else return dl;
            })
        );
    };

    const handleOverride = (deadline, checked) => {
        setDeadlines(
            deadlines.map((dl) => {
                if (dl.id === deadline.id) return { ...dl, isEdited: checked };
                else return dl;
            })
        );
    };

    const handleOpen = async () => {
        setSaving(false);

        if (deadlineId) {
            setDeadlines([await getDeadlineForResidentialUnitAndDeadline(residentialUnitId, deadlineId)]);
        } else {
            setDeadlines(await getDeadlinesForResidentialUnit(residentialUnitId));
        }
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen} size="lg" autoFocus={false}>
            <ModalHeader>
                <Trans>Edit deadlines</Trans>
            </ModalHeader>
            <ModalBody>
                <Banner type="info">
                    <Trans>
                        <strong>Edit deadlines </strong>enables you to change the date of an individual deadline already
                        assigned to a unit, to ensure the date is updated remember to click on ‘Save changes’ before you
                        close this modal.
                    </Trans>
                </Banner>
                <div className={theme.section}>
                    <div className={theme.title}>Current deadlines</div>
                    {deadlines.length === 0 && (
                        <Banner icon={faInfoCircle} type="info">
                            <Trans>
                                <strong>Please note</strong> you have no project deadlines, to add a deadline go to
                                <ProjectRelativeLink to="settings/general"> project settings</ProjectRelativeLink>.
                            </Trans>
                        </Banner>
                    )}
                    {orderBy(deadlines, 'name').map((dl) => (
                        <div className={theme.row} key={dl.id}>
                            <div className={theme.text}>{dl.name}</div>
                            <div className={theme.override}>
                                <Checkbox checked={dl.isEdited} onChange={(e) => handleOverride(dl, e.target.checked)}>
                                    <Trans>Override</Trans>
                                </Checkbox>
                            </div>
                            <div className={theme.text}>
                                <DatePicker
                                    onChange={(date) => {
                                        if (date) date.setHours(12);
                                        changeDate(date, dl);
                                    }}
                                    value={new Date(dl.deadline)}
                                    className={classnames('form-control')}
                                    disabled={!dl.isEdited}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <YesNoDialog
                    title={<Trans>Revert all deadlines</Trans>}
                    body={<Trans>Are you sure you want to revert to default deadlines?</Trans>}
                    show={showRevertDialog}
                    onAccept={() => handleRevertAll()}
                    onClose={handleRevertClose}
                    confirmButtonColor={'primary'}
                    cancelButtonColor={'warning'}
                    confirmButtonText={<Trans>Revert</Trans>}
                    cancelButtonText={<Trans>Cancel</Trans>}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    className="left"
                    color="danger"
                    onClick={() => {
                        handleRevertOpen();
                    }}
                    disabled={saving}
                    icon={faHistory}>
                    <Trans>Revert all</Trans>
                </Button>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="primary" onClick={handleAccept} loading={saving}>
                    <Trans>Save</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DeadlineEditDialog;
